<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import usePortalLibrary, { metaLine } from '../../composables/usePortalLibrary';
import useLibraryMarks from '../../composables/useLibraryMarks';

/**
 * Home's library, in two columns (XN-4734): the newest documents not already
 * saved, and what they saved, side by side. Saved appears only when
 * something visible is saved; until then a line under New teaches the star.
 * Without the marks (failed, or staff) New lists the newest and claims
 * nothing about saving. Waits for the marks so New does not shift.
 */
const { filesOn, state, items } = usePortalLibrary();
const marks = useLibraryMarks();
const MAX_ROWS = 4;

const knowsMarks = computed(() => marks.state.value === 'ready');
const ready = computed(() => filesOn.value === true && state.value === 'ready' && marks.state.value !== 'loading');
const saved = computed(() => (knowsMarks.value ? items.value.filter((i) => marks.isSaved(i.id)) : []));
const fresh = computed(() => items.value.filter((i) => !knowsMarks.value || !marks.isSaved(i.id)).slice(0, MAX_ROWS));
const show = computed(() => ready.value && (fresh.value.length > 0 || saved.value.length > 0));
</script>

<template>
  <div v-if="show" class="hlib" data-testid="home-library">
    <section v-if="fresh.length" class="hlib__col" data-testid="home-lib-new">
      <div class="hlib__sh"><h2>New in your library</h2><span class="hlib__ln" /><RouterLink to="/hub/library" class="hlib__all">See all</RouterLink></div>
      <p v-if="knowsMarks && !saved.length" class="hlib__hint">Tap the star on anything to keep it here.</p>
      <div class="hlib__card">
        <RouterLink v-for="item in fresh" :key="item.id" :to="`/hub/library/${item.id}`" class="hlib__row">
          <b>{{ item.name }}</b><span>{{ metaLine(item) }}</span>
        </RouterLink>
      </div>
    </section>
    <section v-if="saved.length" class="hlib__col" data-testid="home-lib-saved">
      <div class="hlib__sh"><h2>Saved by you</h2><span class="hlib__ln" /><RouterLink to="/hub/library?tab=saved" class="hlib__all">See all</RouterLink></div>
      <div class="hlib__card">
        <RouterLink v-for="item in saved.slice(0, MAX_ROWS)" :key="item.id" :to="`/hub/library/${item.id}`" class="hlib__row">
          <b>{{ item.name }}</b><span>{{ metaLine(item) }}</span>
        </RouterLink>
      </div>
    </section>
  </div>
</template>

<style scoped>
.hlib { display: grid; grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); gap: 24px; }
/* Section header as Coming up's: prototype `.sh`, 34px above. */
.hlib__sh { display: flex; align-items: center; gap: 12px; margin: 34px 0 14px; }
.hlib__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.hlib__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.hlib__all { font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); text-decoration: none; }
.hlib__hint { margin: -6px 0 12px; font-size: 13px; color: var(--xl-color-outline); }
.hlib__card { background: #fff; border: 1px solid var(--xl-color-border); border-radius: 24px; box-shadow: var(--xl-shadow-1); padding: 6px 22px; }
.hlib__row { display: grid; gap: 2px; padding: 12px 0; border-bottom: 1px solid var(--xl-color-border-subtle); text-decoration: none; color: inherit; }
.hlib__row:last-child { border-bottom: 0; }
.hlib__row b { font-size: 15px; font-weight: 500; color: var(--xl-color-primary-text); overflow-wrap: anywhere; }
.hlib__row span { font-size: 13px; color: var(--xl-color-outline); }
@media (max-width: 700px) { .hlib { grid-template-columns: 1fr; } }
</style>
